import {
  ReportSchool,
  SchoolMidRange,
  SchoolStanding,
  TargetTest,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';

export const deriveStanding = (score: number, mid50: SchoolMidRange): SchoolStanding => {
  if (score < mid50.low) return 'below';
  if (score > mid50.high) return 'above';
  // A school can report both bounds without the middle, leaving the half unknowable.
  if (mid50.median === null) return 'in_range';

  return score < mid50.median ? 'lower_half' : 'upper_half';
};

/**
 * Which school's mid-50% the chosen target reaches, for the target-stepper context line.
 * `reached` = the target is not below this school's mid-50% (the highest such school).
 * `nearest` = no school is reached yet; the closest school above the target and how far off it is.
 */
export type SchoolContext =
  | { kind: 'reached'; school: string | null; low: number }
  | { kind: 'nearest'; school: string | null; low: number; pointsAway: number };

/**
 * Derive the context line from the tiered schools and the chosen target, for the plan's test.
 * Returns null when there is no target, or no school reports a mid-50% range for that test.
 *
 * @param schools - The report's tiered schools (each with both tests' mid-50% ranges).
 * @param test - The plan's test; picks the ACT or SAT range per school.
 * @param targetScore - The currently chosen target score, or null.
 */
export const deriveSchoolContext = (
  schools: readonly ReportSchool[],
  test: TargetTest,
  targetScore: number | null,
): SchoolContext | null => {
  if (targetScore === null) return null;

  const candidates = schools
    .map((school) => {
      const range = test === 'act' ? school.actMid50 : school.satMid50;

      return range ? { name: school.name, range } : null;
    })
    .filter(
      (candidate): candidate is { name: string | null; range: SchoolMidRange } =>
        candidate !== null,
    );

  if (candidates.length === 0) return null;

  // Highest school the target already reaches into.
  const reached = candidates
    .filter((candidate) => deriveStanding(targetScore, candidate.range) !== 'below')
    .sort((a, b) => b.range.low - a.range.low)[0];

  if (reached) return { kind: 'reached', school: reached.name, low: reached.range.low };

  // Otherwise the nearest school still above the target.
  const nearest = [...candidates]
    .sort((a, b) => a.range.low - b.range.low)
    .find((candidate) => candidate.range.low > targetScore);

  if (nearest) {
    return {
      kind: 'nearest',
      school: nearest.name,
      low: nearest.range.low,
      pointsAway: nearest.range.low - targetScore,
    };
  }

  return null;
};
